import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { MARKDOWN_TABLE_SCROLL_CLASS } from "./markdownToHtml";

/**
 * Card + horizontal-scroll shell for an answer table, applied on the element
 * that CONTAINS the `.md-table-scroll` wrapper — the message bubble for tables
 * that stay raw HTML, `AskHelenaAnswerTable` for the sortable ones.
 *
 * SafeHtmlRenderer's base styles are tuned for forwarded-email layout tables
 * (fixed layout, no chrome), which collapses a Markdown table into unreadable
 * runs of text. sx is merged after those, so these win.
 */
export const markdownTableSx = (theme: Theme): SystemStyleObject<Theme> => ({
  [`& .${MARKDOWN_TABLE_SCROLL_CLASS}`]: {
    margin: "12px 0",
    border: `1px solid ${theme.palette.neutral["015"]}`,
    borderRadius: "8px",
    backgroundColor: theme.palette.neutral.main,
    overflowX: "auto",
    overscrollBehaviorX: "contain",
    // Slim but visible — a hidden bar gives no hint the table scrolls.
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { height: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.neutral["020"],
      borderRadius: "4px",
    },
  },
  // `max-content` + `min-width: 100%`: cells size to their longest line so
  // nothing wraps, and the card still fills the column when the table is
  // narrow. Overflow goes to the wrapper's horizontal scrollbar.
  "& table": {
    width: "max-content",
    minWidth: "100%",
    tableLayout: "auto",
    borderCollapse: "collapse",
  },
  "& th, & td": {
    padding: "12px 16px",
    textAlign: "left",
    verticalAlign: "top",
    borderBottom: `1px solid ${theme.palette.neutral["010"]}`,
    // Base styles force break-word on cells — that's what shredded the
    // columns. Scroll instead of breaking.
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
  },
  "& th": {
    ...theme.typography.caption,
    color: theme.palette.lightBg.high,
  },
  "& td": {
    ...theme.typography["body-small"],
    color: theme.palette.lightBg.high,
  },
  "& tbody tr:last-of-type td": {
    borderBottom: "none",
  },
});
